import React from "react";
import { Box, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeftRounded as ChevronLeftIcon,
  HomeRounded as HomeIcon,
} from "@mui/icons-material";

/* ── Back + Dashboard ─────────────────────────────────────────────────────────
 * The pair every pawn screen carries at the TOP-LEFT of its header, immediately
 * left of the screen name — the shape PawnCustomerMaster and the Cloud
 * Accounting screens already used, now shared so all of them stay identical.
 *
 * Why in each screen's header and not in PawnLayout's chrome: the rail expands
 * on hover, so a pair parked in the collapsed icon column gets swapped out from
 * under the pointer exactly as you reach for it. A header is the one anchor
 * that is in the same place at every width and never moves.
 *
 * Two paint variants:
 *   default  — solid blue chips, for headers on a white/near-white surface
 *   onDark   — translucent white chips, for the blue gradient headers
 *
 * `accent` / `accentHover` override the blue for a screen that runs on its own
 * accent — the settlement sheet is the receipt sheet with every blue swapped for
 * green, so it passes its CSS vars through and the chips follow.
 */

const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";

const PawnNavButtons = ({
  onDark = false,
  size = 32,
  gap = 1,
  accent = BLUE,
  accentHover = BLUE_DARK,
  sx,
}) => {
  const navigate = useNavigate();

  const goBack = () => {
    /* React Router stamps an index onto every history entry it pushes. 0 — or
     * nothing at all — means this screen was opened cold: a deep link, a fresh
     * tab, a scanned QR. There is no entry of ours behind it, so `-1` would walk
     * straight out of the app; land on the dashboard instead. */
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/pawn/home");
  };

  const chipSx = {
    width: size,
    height: size,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: 2,
    bgcolor: onDark ? "rgba(255,255,255,0.2)" : accent,
    transition: "background-color 0.2s ease",
    "&:hover": {
      bgcolor: onDark ? "rgba(255,255,255,0.32)" : accentHover,
    },
    // Kills the grey flash Android paints over a tapped element.
    WebkitTapHighlightColor: "transparent",
  };

  const iconSx = { fill: "#FFF", fontSize: size * 0.66 };

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap, flexShrink: 0, ...sx }}
    >
      <Tooltip title="Back" arrow>
        <Box onClick={goBack} sx={chipSx}>
          <ChevronLeftIcon sx={iconSx} />
        </Box>
      </Tooltip>
      <Tooltip title="Dashboard" arrow>
        <Box onClick={() => navigate("/pawn/home")} sx={chipSx}>
          <HomeIcon sx={iconSx} />
        </Box>
      </Tooltip>
    </Box>
  );
};

export default PawnNavButtons;
